/**
 * PsbStage6bPage — Route B TAKEFILE Receipts Import
 *
 * Step 1: Source  — select an applied Stage 6A session (chain: S0→S1→S2→S6A)
 * Step 2: Bridge  — TAKEFILE cross-check: per-slot totals, BARSFILE comparison,
 *                   XTAKFILE net adj, VAT derivation, per-bar sub-totals
 * Step 3: Review  — raw source rows (bar/slot/desc/inc-VAT/exc-VAT/datefrom/addition/adj)
 * Step 4: Apply   — write accounts_sales_incl_vat + excl_vat; provenance ledger
 *
 * Evidence gates enforced at this layer:
 *  - Raw source preservation: take_slot, take_desc, amount_inc_vat, datefrom, addition flag, SHA
 *  - BARSFILE cross-check (xcheck_inc_ok) and VAT derivation (vat_derivation_ok)
 *  - XTAKFILE add/ded netted per-slot; net shown explicitly
 *  - Zero slots shown and counted but not posted
 *  - Existing-data precedence: if accounts_sales_incl_vat IS NOT NULL → skipped_existing
 *  - Draft + Route B (period_to not null) + site 271 guards shown
 *  - Production gate: amber warning banner
 *  - Idempotency: repeat apply = 0 new writes
 *  - URL persistence: ?planId= restores step/plan
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList, Database,
  Loader2, ShieldAlert, AlertTriangle, BadgeCheck, Receipt,
  BarChart3, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Bridge' },
  { id: 3, label: 'Review' },
  { id: 4, label: 'Apply' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
  if (!s) return '—';
  return s;
}

// ── Step nav ─────────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s6b">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s6b-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// ── ReconciliationBadge ───────────────────────────────────────────────────────

function RecBadge({ ok, label }) {
  return ok
    ? <span data-testid="rec-badge-ok" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200"><CheckCircle2 size={12} />{label || 'Pass'}</span>
    : <span data-testid="rec-badge-fail" className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200"><AlertTriangle size={12} />{label || 'Fail'}</span>;
}

function Stat({ label, value, mono, highlight, testid }) {
  const colour = highlight === 'green' ? 'text-emerald-700' : highlight === 'amber' ? 'text-amber-700' : highlight === 'red' ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span data-testid={testid} className={`text-sm font-semibold ${colour} ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ── SlotBreakdownTable ────────────────────────────────────────────────────────

function SlotBreakdownTable({ slotBreakdown }) {
  if (!slotBreakdown || slotBreakdown.length === 0)
    return <p className="text-xs text-slate-400">Slot breakdown not available (Stage 0 data required).</p>;

  const statusMeta = {
    nonzero:        { label: 'Nonzero', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    zero:           { label: 'Explicit zero', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    blank:          { label: 'Blank (key present, empty)', cls: 'bg-slate-100 text-slate-600' },
    absent:         { label: 'Absent (key not in source)', cls: 'bg-slate-50 text-slate-400' },
    'absent/blank': { label: 'Absent/blank', cls: 'bg-slate-50 text-slate-400' },
    absent_unlisted_bar: { label: 'Absent (bar not in TAKEFILE)', cls: 'bg-red-50 text-red-700' },
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="s6b-slot-breakdown-table">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">BAR</th>
            {[1,2,3,4,5].map(s => (
              <th key={s} className="text-center px-2 py-2 font-semibold text-slate-600">TAKE{s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slotBreakdown.map((bar_row, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="px-3 py-1.5 font-mono font-semibold text-slate-700">
                {bar_row.bar}{bar_row.bar_name ? ` (${bar_row.bar_name})` : ''}
              </td>
              {bar_row.slots.map(s => {
                const meta = statusMeta[s.status] || { label: s.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <td key={s.slot} className="px-2 py-1.5 text-center">
                    <div className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.cls}`}>
                      {s.status === 'nonzero' ? `${s.amount_inc_vat ? '£'+parseFloat(s.amount_inc_vat).toLocaleString('en-GB',{minimumFractionDigits:2}) : 'nonzero'}` : meta.label}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-2 border-t border-slate-100 flex flex-wrap gap-3 text-[10px] text-slate-500 bg-slate-50">
        {Object.entries(statusMeta).slice(0,4).map(([k,v]) => (
          <span key={k} className="flex items-center gap-1">
            <span className={`px-1.5 py-0.5 rounded border ${v.cls}`}>{k}</span> = {v.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── FingerprintChain ──────────────────────────────────────────────────────────

function FingerprintChain({ plan, stage, prevLabel }) {
  if (!plan) return null;
  const labels = {
    '6B': ['S0', 'S1', 'S2', 'S6A', 'S6B'],
    '6C': ['S0', 'S1', 'S2', 'S6B', 'S6C'],
  };
  const ids = {
    '6B': [plan.stage0_run_id, plan.s1_run_id, plan.s2_plan_id, plan.s6a_run_id, plan.run_id],
    '6C': [plan.stage0_run_id, plan.s1_run_id, plan.s2_plan_id, plan.s6b_run_id, plan.run_id],
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`s${stage.toLowerCase()}-fingerprint-chain`}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Source Fingerprint Chain</p>
      <div className="flex items-center flex-wrap gap-1">
        {(labels[stage] || []).map((lbl, i) => {
          const id = (ids[stage] || [])[i];
          const shortId = id ? String(id).slice(0, 8) : '—';
          return (
            <React.Fragment key={lbl}>
              <div className="flex flex-col items-center">
                <span className="text-[9px] font-semibold text-slate-500 uppercase">{lbl}</span>
                <code className="text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{shortId}</code>
              </div>
              {i < (labels[stage] || []).length - 1 && <span className="text-slate-300 text-xs font-bold px-0.5">→</span>}
            </React.Fragment>
          );
        })}
        <RecBadge ok={true} label="Chain bound" />
      </div>
    </div>
  );
}

// ── WriteGateCard ─────────────────────────────────────────────────────────────

function WriteGateCard({ bridge }) {
  if (!bridge) return null;
  const ok = bridge.write_gate_ok === true;
  const env = bridge.psb_write_env || '<unset>';
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
      data-testid="s6b-write-gate-card">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Environment Write Gate (independent of site ID)</p>
      <div className="flex items-center gap-3">
        <RecBadge ok={ok} label={ok ? `PSB_WRITE_ENV=${env} — writes permitted` : `PSB_WRITE_ENV=${env} — writes BLOCKED`} />
        <span className="text-xs text-slate-500">Site ID check is a second, separate guard.</span>
      </div>
      {!ok && (
        <p className="text-xs text-red-700 mt-1">
          Set PSB_WRITE_ENV=preview to allow writes. Production writes are blocked regardless of site ID.
        </p>
      )}
    </div>
  );
}


function BridgeCard({ bridge, bars }) {
  if (!bridge) return null;
  const b = bridge;
  return (
    <div className="space-y-4" data-testid="s6b-bridge-card">

      {/* Source summary */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">TAKEFILE Source</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Source bars" value={b.source_bars} mono testid="bridge-source-bars" />
          <Stat label="Slots with data" value={b.source_slots_with_data} mono testid="bridge-slots-data" />
          <Stat label="Importable slots" value={b.importable_slots} mono highlight="green" testid="bridge-importable" />
          <Stat label="Zero slots (stored)" value={b.zero_slots} mono testid="bridge-zero-slots" />
          <Stat label="XTAKFILE net adj" value={fmtGBP(b.xtakfile_net_add)} mono testid="bridge-xtakfile-net" />
          <Stat label="VAT rate" value={`${b.vat_rate}%`} mono testid="bridge-vat-rate" />
          <Stat label="Total inc-VAT" value={fmtGBP(b.total_inc_vat)} mono highlight="green" testid="bridge-total-inc" />
          <Stat label="Total exc-VAT (derived)" value={fmtGBP(b.total_exc_vat)} mono testid="bridge-total-exc" />
        </div>
      </div>

      {/* BARSFILE cross-check */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">BARSFILE Cross-Check</p>
        {bars && bars.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="s6b-barscheck-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">BAR</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">BARSFILE TAKEINC</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600">BARSFILE TAKEEXC</th>
                </tr>
              </thead>
              <tbody>
                {bars.map((bx, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 font-mono">{bx.bar}</td>
                    <td className="px-3 py-1.5 text-slate-600">{bx.bar_name || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(bx.barsfile_takeinc)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(bx.barsfile_takeexc)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                  <td className="px-3 py-1.5 text-slate-600" colSpan={2}>Grand total</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(b.barsfile_total_inc)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(b.barsfile_total_exc)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-slate-400">No BARSFILE rows found.</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">TAKEFILE ≡ BARSFILE TAKEINC:</span>
            <RecBadge ok={b.xcheck_inc_ok} label={b.xcheck_inc_ok ? 'Matches ✓' : 'Mismatch'} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">VAT derivation (TAKEINC÷1.{Math.round(b.vat_rate)} = TAKEEXC):</span>
            <RecBadge ok={b.vat_derivation_ok} label={b.vat_derivation_ok ? 'Correct ✓' : 'Mismatch'} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Overall:</span>
            <RecBadge ok={b.reconciles} label={b.reconciles ? 'Reconciled ✓' : 'Not reconciled'} />
          </div>
        </div>
      </div>

      {/* Source reference (N/A notice) */}
      {b.source_reference && (
        <div className="rounded-lg bg-slate-100 border border-slate-300 px-3 py-2 flex items-start gap-2" data-testid="s6b-source-reference">
          <span className="text-slate-500 mt-0.5 shrink-0 text-xs font-bold">REF</span>
          <p className="text-xs text-slate-700">
            <strong>TAKEFILE document reference:</strong> {b.source_reference}
          </p>
        </div>
      )}

      {/* Target preview */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-3">Fields to Write (Preview)</p>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="accounts_sales_incl_vat" value={fmtGBP(b.total_inc_vat)} mono highlight="green" testid="bridge-target-inc" />
          <Stat label="accounts_sales_excl_vat" value={fmtGBP(b.total_exc_vat)} mono highlight="green" testid="bridge-target-exc" />
        </div>
        <p className="mt-2 text-xs text-emerald-600">
          Precedence: if accounts_sales_incl_vat already set → existing data wins (skipped_existing).
        </p>
      </div>
    </div>
  );
}

// ── RowTable ──────────────────────────────────────────────────────────────────

function RowTable({ rows }) {
  if (!rows || rows.length === 0)
    return <p className="text-sm text-slate-400 py-4">No rows to display.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6b-row-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">BAR</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">TAKE slot</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Description</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Inc-VAT (raw)</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Exc-VAT (derived)</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">XTAK add</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">XTAK ded</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Net inc-VAT</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Date from</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Addition</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">SHA</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono">{r.bar}</td>
              <td className="px-3 py-1.5 font-mono text-slate-700">TAKE{r.take_slot}</td>
              <td className="px-3 py-1.5 text-slate-600">{r.take_desc || '—'}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.amount_inc_vat)}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtGBP(r.amount_exc_vat)}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtGBP(r.xtakfile_add)}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtGBP(r.xtakfile_ded)}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtGBP(r.net_inc_vat)}</td>
              <td className="px-3 py-1.5 font-mono text-slate-500 text-[10px]">{fmtDate(r.datefrom)}</td>
              <td className="px-3 py-1.5">
                {r.addition === true
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">+</Badge>
                  : r.addition === false
                    ? <Badge className="bg-slate-100 text-slate-600 text-[10px]">—</Badge>
                    : <span className="text-slate-300">n/a</span>
                }
              </td>
              <td className="px-3 py-1.5 font-mono text-slate-400 text-[10px]">{r.source_sha256 || '—'}</td>
              <td className="px-3 py-1.5">
                {r.resolution_status === 'importable'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Importable</Badge>
                  : r.resolution_status === 'zero'
                    ? <Badge className="bg-slate-100 text-slate-600 text-[10px]">Zero</Badge>
                    : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{r.resolution_status}</Badge>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── LinkTable ─────────────────────────────────────────────────────────────────

function LinkTable({ links }) {
  if (!links || links.length === 0)
    return <p className="text-sm text-slate-400">No provenance links recorded.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6b-link-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Field</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Value written</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Precedence</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Written at</th>
          </tr>
        </thead>
        <tbody>
          {links.map((lk, i) => (
            <tr key={lk.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono text-slate-700">{lk.field_name}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtGBP(lk.value_written)}</td>
              <td className="px-3 py-1.5">
                {lk.precedence_result === 'written'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Written</Badge>
                  : lk.precedence_result === 'skipped_existing'
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Existing wins</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 text-[10px]">{lk.precedence_result}</Badge>
                }
              </td>
              <td className="px-3 py-1.5 text-slate-400 text-[10px] font-mono">{lk.written_at || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SessionGuards ─────────────────────────────────────────────────────────────

function SessionGuards({ plan }) {
  if (!plan) return null;
  const guards = [
    { label: 'Status', value: plan.session_status || '—', ok: (plan.session_status || '').toLowerCase() === 'draft', okLabel: 'Draft ✓', failLabel: 'Not draft' },
    { label: 'Route', value: plan.period_to ? `Route B (to: ${plan.period_to})` : 'Route A (no period_to)', ok: !!plan.period_to, okLabel: 'Route B ✓', failLabel: 'Route A (unexpected)' },
    { label: 'Site', value: `Site ${plan.target_site_id}`, ok: plan.target_site_id === 271, okLabel: 'Site 271 ✓', failLabel: 'Non-fixture site' },
    { label: 'Source fingerprint', value: plan.stage0_run_id ? plan.stage0_run_id.slice(0, 8) : '—', ok: !!plan.stage0_run_id, okLabel: 'Bound ✓', failLabel: 'Missing' },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="s6b-session-guards">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Session Guards</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {guards.map(g => (
          <div key={g.label} className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-500">{g.label}</span>
            <span className="text-xs font-mono text-slate-700">{g.value}</span>
            <RecBadge ok={g.ok} label={g.ok ? g.okLabel : g.failLabel} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage6bPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  // Step 2/3/4
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [bars, setBars] = useState([]);
  const [links, setLinks] = useState([]);

  // Step 4
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);

  const planIdParam = searchParams.get('planId');
  // Guard: skip URL-restore override when we're in the middle of handleCreatePlan
  const isCreatingRef = React.useRef(false);

  // ── Load plan detail helper ──────────────────────────────────────────────────
  const loadPlanDetail = useCallback(async (planId) => {
    const d = await api.get(`/admin/psb-stage6b/plans/${planId}`);
    setPlan(d.data.plan);
    setRows(d.data.rows || []);
    setBars(d.data.bars || []);
    setLinks(d.data.links || []);
    return d.data;
  }, []);

  // ── Restore from URL ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!planIdParam) return;
    if (isCreatingRef.current) return;   // skip: handleCreatePlan is setting the URL
    (async () => {
      try {
        const d = await loadPlanDetail(planIdParam);
        if (d.plan.status === 'applied') {
          setStep(4);
        } else {
          setStep(3);
        }
      } catch {
        /* plan not found — stay on step 1 */
      }
    })();
  }, [planIdParam, loadPlanDetail]);

  // ── Step 1: load sessions ────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 1) return;
    (async () => {
      setLoading(true);
      try {
        const d = await api.get('/admin/psb-stage6b/s6a-sessions');
        setSessions(d.data.sessions || []);
      } catch {
        toast.error('Failed to load Stage 6A sessions');
      } finally {
        setLoading(false);
      }
    })();
  }, [step]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleCreatePlan = useCallback(async () => {
    if (!selectedSession) return;
    setLoading(true);
    try {
      const d = await api.post('/admin/psb-stage6b/plans', {
        stage0_run_id: selectedSession.stage0_run_id,
        s1_run_id: selectedSession.s1_run_id,
        s2_plan_id: selectedSession.s2_plan_id,
        s6a_run_id: selectedSession.s6a_run_id,
        target_session_id: selectedSession.target_session_id,
        site_id: selectedSession.target_site_id,
      });
      const detail = await loadPlanDetail(d.data.plan.run_id);
      if (d.data.idempotent) toast.info('Existing dry-run plan loaded (idempotent)');
      // Set flag BEFORE setSearchParams so URL-restore useEffect skips the override
      isCreatingRef.current = true;
      setSearchParams({ planId: d.data.plan.run_id });
      setStep(2);
      // Enrich plan with session context for guards display
      setPlan(prev => prev ? { ...prev, session_status: selectedSession.session_status, period_to: selectedSession.period_to } : prev);
      // Clear flag after next tick so URL-restore useEffect resumes for fresh navigations
      setTimeout(() => { isCreatingRef.current = false; }, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  }, [selectedSession, loadPlanDetail, setSearchParams]);

  const handleApply = useCallback(async () => {
    if (!plan) return;
    setApplying(true);
    try {
      const d = await api.post(`/admin/psb-stage6b/plans/${plan.run_id}/apply`);
      setApplyResult(d.data);
      setLinks(d.data.links || []);
      setPlan(prev => prev ? { ...prev, status: 'applied' } : prev);
      setStep(4);
      const written = d.data.written || 0;
      if (d.data.idempotent) {
        toast.info('Already applied — no new writes (idempotent)');
      } else {
        toast.success(`Stage 6B applied — ${written} field${written !== 1 ? 's' : ''} written`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [plan]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt size={20} className="text-indigo-600" />
            PSB Stage 6B — TAKEFILE Receipts Import
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Post TAKEFILE takings into session accounts_sales · Preview only · Site 271
          </p>
        </div>
      </div>

      <StepNav current={step} />

      {/* Step 1 — Source */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database size={16} className="text-indigo-500" />Select Stage 6A Source Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Select an applied Stage 6A session. The full S0→S1→S2→S6A fingerprint chain will be validated before the TAKEFILE plan is created.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <ShieldAlert size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">Preview only — writes gated to site 271. Production writes remain disabled.</p>
            </div>

            {loading && <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><Loader2 size={16} className="animate-spin" />Loading sessions…</div>}

            {!loading && sessions.length === 0 && (
              <p className="text-sm text-slate-400">No applied Stage 6A sessions found. Complete Stage 6A first.</p>
            )}

            {!loading && sessions.map(s => (
              <div
                key={s.s6a_run_id}
                onClick={() => setSelectedSession(s)}
                className={`cursor-pointer rounded-xl border p-4 transition-all ${selectedSession?.s6a_run_id === s.s6a_run_id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                data-testid={`session-card-${s.target_session_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{s.session_reference}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Session {s.target_session_id} · Site {s.target_site_id} · {s.period_from} → {s.period_to}</p>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">S6A: {s.s6a_run_id.slice(0, 8)} · S0: {s.stage0_run_id.slice(0, 8)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">S6A Applied</Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-[10px]">{s.session_status}</Badge>
                    {s.period_to && <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">Route B</Badge>}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleCreatePlan}
                disabled={!selectedSession || loading}
                data-testid="create-plan-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {loading ? <><Loader2 size={15} className="animate-spin mr-1" />Building plan…</> : <>Run dry-run <ChevronRight size={15} className="ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Bridge */}
      {step === 2 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 size={16} className="text-indigo-500" />TAKEFILE Bridge & Cross-Check
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Plan:</span>
              <code className="bg-slate-100 rounded px-1.5 py-0.5 font-mono" data-testid="plan-id-display">{plan.run_id.slice(0, 8)}</code>
              <span>→ Session {plan.target_session_id} · Site {plan.target_site_id}</span>
              {plan.stage0_run_id && <>
                <span>·</span>
                <span className="font-mono text-slate-400">S0: {plan.stage0_run_id.slice(0, 8)}</span>
              </>}
            </div>

            <SessionGuards plan={{ ...plan, session_status: selectedSession?.session_status || plan.session_status, period_to: selectedSession?.period_to || plan.period_to }} />
            <BridgeCard bridge={plan.bridge} bars={bars} />

            {/* ── Evidence Gap 1: Write Gate ─────────────────────────────── */}
            <WriteGateCard bridge={plan.bridge} />

            {/* ── Evidence Gap 2: S0 Fingerprint Chain ───────────────────── */}
            <FingerprintChain plan={plan} stage="6B" />

            {/* ── Evidence Gap 4: TAKEFILE 5-Bar × 5-Slot Breakdown ──────── */}
            {plan.bridge?.slot_breakdown && plan.bridge.slot_breakdown.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="s6b-slot-breakdown-section">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  TAKEFILE Slot Reconciliation — All 5 Bars × 5 Slots
                </p>
                <SlotBreakdownTable slotBreakdown={plan.bridge.slot_breakdown} />
                <p className="mt-2 text-xs text-slate-400">
                  Zero slots are stored and counted but not posted to accounts_sales.
                  Absent/blank slots are preserved exactly as received from TAKEFILE source.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setPlan(null); setSearchParams({}); }} data-testid="back-to-source-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              <Button onClick={() => setStep(3)} data-testid="proceed-to-review-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Review rows <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Review */}
      {step === 3 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList size={16} className="text-indigo-500" />Review TAKEFILE Rows
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">
              All source rows are shown below with raw values preserved: slot key, description, inc-VAT amount, XTAKFILE adjustments, derived exc-VAT, date, and addition flag.
            </p>

            <RowTable rows={rows} />

            {/* Row count summary */}
            {plan.bridge && (
              <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
                <span data-testid="total-slots">Total: {rows.length} row{rows.length !== 1 ? 's' : ''}</span>
                <span data-testid="importable-slots">Importable: {plan.bridge.importable_slots}</span>
                <span data-testid="zero-slots">Zero: {plan.bridge.zero_slots}</span>
              </div>
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-bridge-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              {plan.status === 'applied' ? (
                <Button onClick={() => setStep(4)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="view-apply-result-btn">
                  <CheckCircle2 size={15} className="mr-1" />View result
                </Button>
              ) : (
                <Button
                  onClick={handleApply}
                  disabled={applying || (plan.bridge?.importable_slots || 0) === 0}
                  data-testid="apply-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {applying ? <><Loader2 size={15} className="animate-spin mr-1" />Applying…</> : <>Apply Stage 6B <ChevronRight size={15} className="ml-1" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Apply result */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck size={16} className="text-emerald-500" />Stage 6B Applied
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">

            {/* Apply summary */}
            {applyResult && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="s6b-apply-result">
                <p className="text-sm font-semibold text-emerald-800">
                  Receipts written to session {applyResult.target_session_id}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="Fields written" value={applyResult.written} highlight={applyResult.written > 0 ? 'green' : undefined} testid="apply-written" />
                  <Stat label="Existing wins (skipped)" value={applyResult.skipped_existing} testid="apply-skipped" />
                  <Stat label="accounts_sales_incl_vat" value={fmtGBP(applyResult.total_inc_vat)} mono highlight="green" testid="apply-incl-vat" />
                  <Stat label="accounts_sales_excl_vat" value={fmtGBP(applyResult.total_exc_vat)} mono highlight="green" testid="apply-excl-vat" />
                </div>
                {applyResult.idempotent && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid="idempotent-notice">
                    Idempotent — plan was already applied. Zero new writes.
                  </p>
                )}
              </div>
            )}

            {/* Show stored values from plan bridge if apply result not in state (URL restore) */}
            {!applyResult && plan?.bridge && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">Stored values (restored from plan)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="accounts_sales_incl_vat" value={fmtGBP(plan.bridge.total_inc_vat)} mono highlight="green" testid="apply-incl-vat" />
                  <Stat label="accounts_sales_excl_vat" value={fmtGBP(plan.bridge.total_exc_vat)} mono highlight="green" testid="apply-excl-vat" />
                </div>
              </div>
            )}

            {/* Bridge recap */}
            {plan?.bridge && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Bridge Recap</p>
                <BridgeCard bridge={plan.bridge} bars={bars} />
              </div>
            )}

            {/* Provenance ledger */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Provenance Ledger</p>
              <LinkTable links={links} />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} data-testid="back-to-review-btn">
                <ArrowLeft size={15} className="mr-1" />Review
              </Button>
              <Button variant="outline" onClick={() => navigate('/stocktaking/sessions')} data-testid="view-session-btn">
                View session
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
